import classNames from "classnames";
import React, { CSSProperties, ReactNode } from "react";

import { Alert } from "../alert";
import css from "./data_table.module.scss";

export interface DataTableColumn {
  if?: boolean;
  renderHeader?: () => ReactNode;
  renderCell?: (row: any) => ReactNode;
  cellKey?: string;
  headerName?: string;
  qaId?: string;
  narrow?: boolean;
  tdStyle?: CSSProperties;
}

interface Props {
  data: any[];
  columns: DataTableColumn[];
  qaId?: string;
  renderFocusRow?: (row: any) => ReactNode;
  rounded?: boolean;
  withSpacing?: boolean;
  style?: CSSProperties;
  tbodyStyle?: CSSProperties;
  warnings?: Warning[];
}

export interface Warning {
  key: string;
  message: string;
}

const RowWarningMessage = ({ message }) => (
  <tr className={css.tr}>
    <td colSpan={3}>
      <Alert title={message} small />
    </td>
  </tr>
);

const DataTable = ({
  data,
  columns,
  qaId = "DataTable",
  renderFocusRow,
  rounded = false,
  withSpacing = false,
  style,
  tbodyStyle,
  warnings
}: Props): JSX.Element => (
  <div className={css.root} data-qa-id={qaId} style={style}>
    <table className={classNames(css.table)}>
      <thead>
        <tr>
          {columns.map((column, thIndex) => {
            if (column.if === false) {
              return false;
            }
            const className = classNames(css.th, { [css.narrow]: !!column.narrow });
            return (
              <th
                key={thIndex}
                className={classNames(className, { [css.withSpacing]: withSpacing })}
                data-qa-id={column.qaId}
              >
                {column.renderHeader && column.renderHeader()}
                {column.headerName || ""}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody style={tbodyStyle}>
        {data &&
          data.map((datum, rowIndex) => {
            const focusRow = renderFocusRow && renderFocusRow(datum);
            return (
              <React.Fragment key={datum.id || rowIndex}>
                <>
                  <tr data-qa-id="DataTable.row" className={css.tr}>
                    {columns.map((column, colIndex) => {
                      if (column.if === false) {
                        return false;
                      }
                      return (
                        <td
                          key={colIndex}
                          style={column.tdStyle}
                          className={classNames(css.td, {
                            [css.narrow]: column.narrow,
                            [css.rounded]: rounded,
                            [css.withSpacing]: withSpacing
                          })}
                        >
                          {!!column.cellKey && datum[column.cellKey]}
                          {!!column.renderCell && column.renderCell(datum)}
                        </td>
                      );
                    })}
                  </tr>
                  {focusRow && (
                    <tr>
                      <td colSpan={1000}>{focusRow}</td>
                    </tr>
                  )}
                </>
                {warnings?.length &&
                  warnings
                    .filter((warning) => datum.name === warning.key)
                    .map((warning) => <RowWarningMessage message={warning.message} key={warning.key} />)}
              </React.Fragment>
            );
          })}
      </tbody>
    </table>
  </div>
);

export default DataTable;
